import jwtDecode from 'jwt-decode';
import { route } from 'preact-router';

import { IAuthDecodedPayload, IAuthResponse } from '@slottracker/utils';

import { postMapped } from './api';
import * as cookie from './cookie';

const AUTH_DATA_STORAGE_KEY = 'authData-v2';
const USER_STORAGE_KEY = 'user-v2';
const clearAuthData = (): void => {
    cookie.removeCookieCrossDomain(AUTH_DATA_STORAGE_KEY);
    cookie.removeCookieCrossDomain(USER_STORAGE_KEY);
};
const clearAuthDataAndRedirectToAuth = (): void => {
    clearAuthData();
    route('/login');
};
const getAuthData = (): IAuthResponse | null => {
    const authData: IAuthResponse | null = cookie.getCookie(AUTH_DATA_STORAGE_KEY);

    if (!authData) {
        return null;
    }

    if (
        typeof authData !== 'object'
        || typeof authData.accessToken !== 'string'
        || typeof authData.refreshToken !== 'string'
    ) {
        clearAuthDataAndRedirectToAuth();

        return null;
    }

    return authData;
};
const getUser = (): object | null => cookie.getCookie<object>(USER_STORAGE_KEY);

export const isAuthenticated = (): boolean => {
    const authData = getAuthData();
    const userData = getUser();

    return Boolean(authData) && Boolean(userData);
};

export const getAccessToken = (): string | null => getAuthData()?.accessToken ?? null;

export const getRefreshToken = (): string | null => getAuthData()?.refreshToken ?? null;

export const refreshToken = async (): Promise<void> => {
    const body = {
        accessToken: getAccessToken(),
        refreshToken: getRefreshToken(),
    };

    try {
        const data = await postMapped<IAuthResponse>('/v2/auth/refresh-token', body);

        if (!data) {
            throw new Error('Refresh token fetching error.');
        }

        const decodedAccessToken = jwtDecode<IAuthDecodedPayload>(data.accessToken);
        const decodedRefreshToken = jwtDecode<IAuthDecodedPayload>(data.refreshToken);
        const cookieExpires = decodedRefreshToken.exp;
        const userDetails = decodedAccessToken.sub;

        cookie.setCookieCrossDomain(AUTH_DATA_STORAGE_KEY, data, cookieExpires);
        cookie.setCookieCrossDomain(USER_STORAGE_KEY, userDetails, cookieExpires);
    } catch (err: unknown) {
        clearAuthDataAndRedirectToAuth();

        throw err;
    }
};
